import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { CheckCircle2, AlertTriangle, X } from 'lucide-react'
import { NOTICE_DURATION_MS, NoticeContext, type NoticeKind, type Notify } from './useNotify'

interface Notice {
  // Identity, not the text: the same message raised twice is two notices, so
  // the second one re-enters and restarts its timer.
  id: number
  kind: NoticeKind
  message: string
}

const SURFACE_SPRING = { type: 'spring', stiffness: 500, damping: 30 } as const
const BUTTON_SPRING = { type: 'spring', stiffness: 400, damping: 20 } as const

// Success is the plum the wordmark wears; an error is the danger brick. Both
// fills carry white text, so the two read apart at a glance and neither can
// be mistaken for a page card.
const KIND_CLASSES: Record<NoticeKind, string> = {
  success: 'bg-plum text-white',
  error: 'bg-danger text-white',
}

/**
 * Provides `useNotify` and renders the one notice surface: fixed at the top
 * centre of the viewport, above page content and modals, below the iOS
 * shell's status bar. One notice at a time — a new one replaces whatever is
 * showing.
 */
export const NoticeProvider = ({ children }: { children: ReactNode }) => {
  const [notice, setNotice] = useState<Notice | null>(null)
  const nextId = useRef(0)

  const raise = useCallback((kind: NoticeKind, message: string) => {
    nextId.current += 1
    setNotice({ id: nextId.current, kind, message })
  }, [])

  const dismiss = useCallback(() => setNotice(null), [])

  // Stable across renders, so pages can list `notify` in effect deps.
  const notify = useMemo<Notify>(() => ({
    success: (message) => raise('success', message),
    error: (message) => raise('error', message),
  }), [raise])

  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(
      () => setNotice(current => (current?.id === notice.id ? null : current)),
      NOTICE_DURATION_MS[notice.kind],
    )
    return () => clearTimeout(timer)
  }, [notice])

  return (
    <NoticeContext.Provider value={notify}>
      {children}
      <div
        // Fixed and inset 16px from both edges, so it can never widen the
        // page on a phone; pointer-events only on the card itself, so the
        // empty band doesn't swallow taps on the nav under it.
        className="pointer-events-none fixed inset-x-4 flex justify-center"
        style={{ top: 'calc(0.75rem + var(--shell-inset-top, 0px))', zIndex: 1300 }}
      >
        <AnimatePresence mode="wait">
          {notice && (
            <motion.div
              key={notice.id}
              data-testid="app-notice"
              data-kind={notice.kind}
              role={notice.kind === 'error' ? 'alert' : 'status'}
              initial={{ opacity: 0, scale: 0.95, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={SURFACE_SPRING}
              style={{ transformOrigin: 'top center' }}
              className={`pointer-events-auto flex w-full max-w-md min-w-0 items-start gap-3 rounded-xl px-4 py-3 shadow-lg ${KIND_CLASSES[notice.kind]}`}
            >
              {notice.kind === 'error'
                ? <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
                : <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />}
              <p className="min-w-0 flex-1 break-words text-sm font-medium">{notice.message}</p>
              <motion.button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                transition={BUTTON_SPRING}
                className="-mr-1 shrink-0 rounded-full p-0.5 text-white/80 hover:text-white"
              >
                <X className="h-5 w-5" strokeWidth={2} />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </NoticeContext.Provider>
  )
}
